// ============================================================
// sectionFlow.ts
// Visibility-aware helpers for the soft-guided section flow.
//
// useSectionCompletion tracks WHICH sections are complete/unlocked,
// but it doesn't know which are currently visible (that's driven by
// viewingPaneToggles in the casevo store). These helpers bridge the
// two so each section component can answer "what's next in line for
// me to scroll to?" without re-implementing the lookup.
// ============================================================
import { CompletableSection, COMPLETABLE_SECTIONS } from '../store/useSectionCompletion'
import { hasAnyInjury } from './validation'
import { VIEWING_PANE_HEADINGS } from '../constants/viewingPane.constants'
import { ViewingPaneToggles, UserRole, ViewingPaneHeading, Case } from '../types/case.types'
import { useCasevoStore } from '../store/useCasevoStore'
import { flushAutoSave } from '../hooks/useAutoSave'
import { flushClientAutoSave } from '../hooks/useClientAutoSave'
import { flushClientNotesAutoSave } from '../hooks/useClientNotesAutoSave'
import { flushWitnessAutoSave } from '../hooks/useWitnessAutoSave'
import { flushDeadlineAutoSave } from '../hooks/useCaseDeadlineAutoSave'
// Filter the sequential chain to whichever sections are currently
// visible to the user (toggle-on, and not admin-only when the user
// isn't an admin). The returned order is the display order — so the
// "next" section is just the next item in this array.
export const getVisibleSequentialKeys = (
  toggles: ViewingPaneToggles,
  userRole: UserRole,
): CompletableSection[] => {
  return COMPLETABLE_SECTIONS.filter((key) => {
    const heading = VIEWING_PANE_HEADINGS.find((h) => h.key === key)
    if (!heading) return false
    if (heading.adminOnly && userRole !== 'admin') return false
    return toggles[key as keyof ViewingPaneToggles]
  })
}
// Find the section that should be unlocked when `thisKey` is marked
// complete. Returns undefined when `thisKey` is the last visible
// sequential section.
export const getNextSequentialKey = (
  thisKey: CompletableSection,
  visibleSequential: CompletableSection[],
): CompletableSection | undefined => {
  const idx = visibleSequential.indexOf(thisKey)
  if (idx === -1 || idx === visibleSequential.length - 1) return undefined
  return visibleSequential[idx + 1]
}
// Find the previous visible sequential section — used to compose
// the "Complete X first" subtext on a locked header.
export const getPrevSequentialKey = (
  thisKey: CompletableSection,
  visibleSequential: CompletableSection[],
): CompletableSection | undefined => {
  const idx = visibleSequential.indexOf(thisKey)
  if (idx <= 0) return undefined
  return visibleSequential[idx - 1]
}
// Human-readable label for a section (the same label rendered in
// the section header). Falls back to the key if the heading config
// somehow doesn't know it.
export const getSectionLabel = (key: ViewingPaneHeading): string => {
  return VIEWING_PANE_HEADINGS.find((h) => h.key === key)?.label ?? key
}
// ── SECTION STATUS (case-detail side-nav icons) ──────────────
// The nav shows a per-section status. ✓ complete is a HUMAN sign-off
// (completedSections) and is NEVER auto-derived. The only NEW signal
// added here is sectionHasData — a lightweight "has ANY data?" check that
// distinguishes IN-PROGRESS (data entered, not yet signed off) from
// EMPTY. It is NOT validation: a section can have data and still be
// invalid (that's the separate 'attention' state from validateCase).

// Per-section data that lives OUTSIDE the Case object (in their own store
// slices). The caller (ViewingPane) supplies the counts.
export interface SectionDataExtras {
  witnessCount: number
  documentCount: number
}

// Minimal "does this completable section have any meaningful data?".
// A few key fields per section, not exhaustive — enough to tell an
// untouched section from a started one. Reuses the Case shape the
// section editors write to.
export const sectionHasData = (
  key: CompletableSection,
  c: Case,
  extras: SectionDataExtras,
): boolean => {
  switch (key) {
    case 'clientSource': {
      const s = c.clientSource
      // "Not applicable" is a deliberate answer → counts as data.
      return s.notApplicable
        || !!(s.name.trim() || s.contactNumber.trim() || s.emailAddress.trim() || s.addressPostcode.trim())
    }
    case 'clientDetails': {
      const d = c.clientDetails
      return !!(d.forename.trim() || d.surname.trim() || d.dateOfBirth
        || d.mainContactNumber.trim() || d.emailAddress.trim())
    }
    case 'accidentDetails': {
      const a = c.accidentDetails
      return !!(a.accidentDate || a.accidentLocation.trim() || a.accidentLocationFreeText.trim()
        || a.accidentLocationAddressPostcode.trim() || a.claimantReg.trim())
    }
    case 'injuryDetails': {
      const i = c.injuryDetails
      return hasAnyInjury(i)
    }
    case 'witnesses':
      return extras.witnessCount > 0
    case 'defendant': {
      const d = c.defendant
      return !!(d.name.trim() || d.reg.trim() || d.mainContactNumber.trim() || d.address)
    }
    case 'tpi': {
      const t = c.tpi
      return !!(t.insurer.trim() || t.referenceNumber.trim() || t.address)
    }
    case 'linkedCases':
      // Real links live in caseLinks (the linking endpoints); the legacy
      // linkedCases array is no longer written, so it never showed progress.
      return (c.caseLinks?.length ?? 0) > 0 || c.linkedCases.length > 0
    case 'medicalAgency':
      return !!(c.medicalAgencyName.trim() || c.medicalAgencyPhone.trim() || c.medicalAgencyEmail.trim()
        || c.medicalAgencyAddressPostcode.trim() || c.medicalAgencyNotes.trim())
    case 'medicalExpert':
      return !!(c.medicalExpertName.trim() || c.medicalExpertPhone.trim() || c.medicalExpertEmail.trim()
        || c.medicalExpertAddressPostcode.trim() || c.medicalExpertNotes.trim())
    case 'tps':
      return !!(c.tpsName.trim() || c.tpsPhone.trim() || c.tpsEmail.trim()
        || c.tpsAddressPostcode.trim() || c.tpsNotes.trim())
    case 'documents':
      return extras.documentCount > 0
    default:
      return false
  }
}

export type SectionStatus =
  | 'complete'      // user-marked-complete (human sign-off)
  | 'in_progress'   // not signed off, but has data
  | 'empty'         // not signed off, no data
  | 'locked'        // sequential + predecessor not complete
  | 'attention'     // validated section with outstanding errors
  | 'neutral'       // non-flow section (deadlines/funding/automatic/history/settings)

// Derive a section's nav status from the EXISTING signals (completion,
// unlock, validation) plus the new hasData flag. No new completion logic
// — this only composes what already exists. Precedence:
//   non-flow → neutral · locked → locked · has errors → attention ·
//   marked complete → complete · has data → in_progress · else → empty.
export const getSectionStatus = (args: {
  isCompletable: boolean
  isComplete: boolean
  isUnlocked: boolean
  hasData: boolean
  hasErrors: boolean
}): SectionStatus => {
  if (!args.isCompletable) return 'neutral'
  if (!args.isUnlocked) return 'locked'
  if (args.hasErrors) return 'attention'
  if (args.isComplete) return 'complete'
  if (args.hasData) return 'in_progress'
  return 'empty'
}

// Advance the case-detail panel to a section (the guided-flow "continue"
// target AND the nav-click target). Flushes every pending auto-save stream
// first so the section we're leaving persists before its panel unmounts,
// then sets the store's activeCaseSection — a clean panel swap. Replaces
// the old scrollToAndFocus (which scrolled the vertical stack; obsolete now
// that only one section is mounted at a time).
// ★ Switches the section IMMEDIATELY, then flushes in the background. It used
// to await every flush BEFORE switching, so when a save was slow or went
// through 409 recovery (re-fetch + re-apply) the click appeared to do nothing
// — the "frozen" screen on rapid "Mark as complete". Switching sections
// doesn't unmount the case, so the autosaves carry on regardless; the flush is
// still kicked off here so the section's edits persist promptly. Never throws.
export const advanceCaseSection = async (sectionKey: ViewingPaneHeading): Promise<void> => {
  // A section is a place: one history step, so Back returns to the section
  // you were on (pushViewHistory is synchronous — the switch stays instant).
  if (useCasevoStore.getState().activeCaseSection !== sectionKey) useCasevoStore.getState().pushViewHistory()
  useCasevoStore.getState().setActiveCaseSection(sectionKey)
  await Promise.allSettled([
    flushAutoSave(),
    flushClientAutoSave(),
    flushClientNotesAutoSave(),
    flushWitnessAutoSave(),
    flushDeadlineAutoSave(),
  ])
}
